// AgentsListPanel — rebuilt 2026-05-16 for the Entity Redesign
// (Step 4). The agent card now surfaces the operational state a
// support admin needs without drilling into the profile:
//
//   • Avatar + @username + Lx level pill + #id
//   • UAID with copy-to-clipboard button (8-char ID, R5 transfer key)
//   • Phone · email contact line
//   • Status pill + state toggles (player-accepting / agent-accepting)
//   • Identity-lock chip + auto-suspend / locked-out chips
//   • Money strip: pool balance + deposit progress (if cap > 0)
//   • Downline strip: direct/indirect agents · direct/indirect players
//   • Last login (relative + absolute on tap)
//
// Per-row kebab opens an EntityActionSheet with:
//
//   • Open profile / Reassign parent / Topup pool / Withdraw
//   • Suspend / Activate
//   • Toggle player-accepting / Toggle agent-accepting
//   • Identity-lock toggle
//   • Force logout / Reset password / Clear lockout
//   • Mark/Unmark test account (admin only)
//   • Delete (admin only)
//
// Bulk mode: select up to 50 rows for bulk-suspend (blueprint Section 7
// hard cap).
//
// Light + dark designed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { levelName } from '../../lib/levelNames';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
  isGeoBagComplete,
} from '@baaligames/shared-ui/components/LocationCapture';
import AgentReassignSheet from '../../components/AgentReassignSheet';
import EntityActionSheet, { type ActionGroup } from '../../components/EntityActionSheet';
import DirectWalletAdjustModal from '../../components/DirectWalletAdjustModal';
import { INPUT_CLS } from '../../components/fieldStyles';
import CommissionRatesFields, { type CommissionRates, ratesPayload } from '@baaligames/shared-ui/admin/components/CommissionRatesFields';

type AgentRow = {
  id: number;
  uaid: string | null;
  username: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  status: 'active' | 'suspended';
  parent_admin_id: number | null;
  parent_agent_id: number | null;
  parent_username: string | null;
  level: number;
  suspended_at: string | null;
  lock_reason: string | null;
  identity_locked?: 0 | 1 | boolean;
  is_accepting_players: 0 | 1 | boolean;
  is_accepting_agents:  0 | 1 | boolean;
  last_login_at: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  is_test: 0 | 1 | boolean;
  wallet_balance: number;
  security_deposit_target_points?: number;
  direct_agents: number;
  indirect_agents: number;
  direct_players: number;
  indirect_players: number;
  created_at: string;
};

const PAGE_SIZE = 24;
const BULK_CAP = 50;

const STATUS_OPTS: Array<{ key: '' | 'active' | 'suspended'; label: string }> = [
  { key: '',          label: 'All' },
  { key: 'active',    label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
];

const STATUS_PILL: Record<string, string> = {
  active:    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  suspended: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30',
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

// Pretty "23 minutes ago" / "2 days ago" from a timestamp.
function relTime(ts: string | null): string {
  if (!ts) return 'never';
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  return `${mo}mo ago`;
}

export default function AgentsListPanel() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const isAdmin = admin?.role === 'admin';
  const [rows, setRows] = useState<AgentRow[]>([]);
  const [resetting, setResetting] = useState<AgentRow | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<'' | 'active' | 'suspended'>('');
  const [testFilter, setTestFilter] = useState<'' | 'hide_test' | 'only_test'>('hide_test');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [reassigning, setReassigning] = useState<AgentRow | null>(null);
  const [actioning, setActioning]   = useState<AgentRow | null>(null);
  const [adjusting, setAdjusting]   = useState<AgentRow | null>(null);
  const [bulkMode, setBulkMode]     = useState(false);
  const [selected, setSelected]     = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy]     = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (debounced) params.set('search', debounced);
      if (status)    params.set('status', status);
      const r = await adminApi.get<{ rows: AgentRow[]; total: number }>(`/admin/hierarchy/agents?${params}`);
      setRows(r.rows); setTotal(r.total);
    } catch (e) { setErr((e as ApiError).message || 'Failed to load agents'); }
    finally { setLoading(false); }
  }, [debounced, status, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(0); }, [debounced, status, testFilter]);
  useEffect(() => { setSelected(new Set()); setBulkResult(null); }, [debounced, status, page, bulkMode, testFilter]);

  const visibleRows = useMemo(() => {
    if (testFilter === 'hide_test') return rows.filter((r) => !r.is_test);
    if (testFilter === 'only_test') return rows.filter((r) =>  r.is_test);
    return rows;
  }, [rows, testFilter]);

  const totalPages   = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom  = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo    = Math.min((page + 1) * PAGE_SIZE, total);

  const headerSubtitle = useMemo(() => {
    if (loading && rows.length === 0) return 'Loading…';
    if (total === 0) return 'No agents yet';
    return `Showing ${showingFrom}–${showingTo} of ${total.toLocaleString('en-IN')}`;
  }, [loading, rows.length, total, showingFrom, showingTo]);

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size >= BULK_CAP) return next;
      else next.add(id);
      return next;
    });
  }

  async function bulkSuspend() {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true); setBulkResult(null);
    const ids = Array.from(selected);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await adminApi.patch(`/admin/agents/${id}`, { status: 'suspended' });
        ok++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkResult(`Suspended ${ok} of ${ids.length}${fail ? ` (${fail} failed)` : ''}`);
    setSelected(new Set());
    await load();
  }

  if (admin?.role !== 'admin') {
    return (
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-6">
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 p-8 text-center">
          <Icon name="lock" size={28} className="mx-auto mb-2" />
          <div className="font-bold">Admin only</div>
          <div className="text-[12px] opacity-80 mt-1">Agents cannot manage other agents.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4 pb-24">
      {/* Header — compact on mobile (icon-only actions), expands on sm+ */}
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="px-2 lg:px-4 pt-3 lg:pt-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="grid place-items-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl shrink-0
                           bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30">
            <Icon name="support_agent" size={20} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-display font-semibold text-neutral-900 dark:text-white tracking-tight leading-tight">
              Agents
            </h1>
            <p className="text-[11.5px] sm:text-[12px] text-arena-600 dark:text-neon-200/70 truncate">{headerSubtitle}</p>
          </div>
          <button
            onClick={() => setBulkMode((v) => !v)}
            className={`shrink-0 inline-flex items-center gap-1 rounded-full text-[12px] font-bold transition active:scale-95
              h-9 w-9 sm:w-auto sm:px-3 justify-center
              ${bulkMode
                ? 'bg-arena-200 text-neutral-900 dark:bg-arena-700 dark:text-white ring-1 ring-inset ring-arena-300/70 dark:ring-arena-500/40'
                : 'bg-white dark:bg-arena-700/40 text-arena-700 dark:text-neon-200/85 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-700/60'}`}
            aria-label={bulkMode ? 'Stop selecting' : 'Start selecting'}
          >
            <Icon name={bulkMode ? 'checklist' : 'select_all'} size={16} />
            <span className="hidden sm:inline">{bulkMode ? 'Selecting' : 'Select'}</span>
          </button>
          <button onClick={() => setCreating(true)}
            className="shrink-0 inline-flex items-center gap-1 rounded-full
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       text-white text-[12px] font-bold h-9 w-9 sm:w-auto sm:px-3 justify-center
                       shadow-[0_2px_10px_rgba(16,185,129,0.35)] transition active:scale-95"
            aria-label="Promote">
            <Icon name="add" size={16} />
            <span className="hidden sm:inline">Promote</span>
          </button>
        </div>
      </motion.div>

      {/* Sticky filter bar */}
      <div className="px-2 lg:px-4 sticky top-0 z-30 -mx-2 lg:-mx-4 pt-1 pb-2 bg-neutral-50/85 dark:bg-arena-900/85 backdrop-blur supports-backdrop-blur:bg-neutral-50/70 supports-backdrop-blur:dark:bg-arena-900/70">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center px-2 lg:px-2">
          <div className="relative flex-1 min-w-0">
            <Icon name="search" size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/70 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents by username, email or UAID…"
              className="w-full rounded-full pl-9 pr-3 py-2 text-[13px]
                         bg-white dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         text-neutral-900 dark:text-white
                         placeholder-arena-400 dark:placeholder-white/40
                         focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/40"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2
                                                                p-1 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-700/70">
                <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/70" />
              </button>
            )}
          </div>
          {/* Filter pills — wrap on mobile, scroll on sm+. Same
             pattern as PlayersListPanel for consistency. */}
          <div className="flex flex-wrap sm:flex-nowrap gap-1.5 sm:overflow-x-auto sm:no-scrollbar">
            {STATUS_OPTS.map((o) => {
              const active = o.key === status;
              return (
                <button
                  key={o.key || 'all'}
                  onClick={() => setStatus(o.key)}
                  className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11.5px] sm:text-[12px] font-semibold transition border
                    ${active
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-[0_2px_10px_rgba(16,185,129,0.35)] dark:bg-white dark:text-neutral-900 dark:border-white'
                      : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}
                >
                  {o.label}
                </button>
              );
            })}
            {(['hide_test','','only_test'] as const).map((tf) => {
              const active = tf === testFilter;
              const label = tf === 'hide_test' ? 'Hide test' : tf === 'only_test' ? 'Only test' : 'All incl. test';
              return (
                <button key={tf || 'all_inc'} onClick={() => setTestFilter(tf)}
                  className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11.5px] sm:text-[12px] font-semibold transition border
                    ${active
                      ? 'bg-amber-500 text-white border-amber-500 dark:bg-amber-400 dark:text-arena-900 dark:border-amber-400'
                      : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {err && (
        <div className="mx-2 lg:mx-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="px-2 lg:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 animate-pulse">
              <div className="h-12 mb-3 bg-arena-100/60 dark:bg-arena-800/40 rounded-xl" />
              <div className="h-4 mb-2 bg-arena-100/60 dark:bg-arena-800/40 rounded" />
              <div className="h-3 mb-2 bg-arena-100/60 dark:bg-arena-800/40 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {!loading && visibleRows.length === 0 && (
        <div className="mx-2 lg:mx-4 rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center">
          <Icon name="support_agent" size={32} className="text-arena-400 dark:text-neon-200/35 mx-auto mb-2" />
          <p className="text-[13px] text-arena-700 dark:text-neon-200/75 font-semibold">
            {search || status || testFilter ? 'No agents match' : 'No agents promoted yet'}
          </p>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/55 mt-0.5">
            {search || status || testFilter
              ? 'Try a different search, status, or test-flag filter.'
              : 'Tap "+ Promote" to onboard your first agent.'}
          </p>
        </div>
      )}

      {visibleRows.length > 0 && (
        <div className="px-2 lg:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {visibleRows.map((r, i) => (
            <AgentCard
              key={r.id} a={r} index={i}
              bulkMode={bulkMode}
              selected={selected.has(r.id)}
              onToggleSelect={() => toggleSelect(r.id)}
              onOpenActions={() => setActioning(r)}
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="px-2 lg:px-4 flex items-center justify-end gap-2 pt-1 text-[12px] text-arena-600 dark:text-neon-200/70">
          <button disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">Page {page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}

      {bulkMode && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[calc(100vw-1.5rem)] max-w-screen-md
                        rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                        shadow-[0_10px_30px_-12px_rgba(0,0,0,0.45)] px-3 py-2.5
                        flex items-center gap-2">
          <Icon name="checklist" size={16} className="text-arena-600 dark:text-neon-200/65" />
          <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white">
            {selected.size} selected
            <span className="ml-1 text-arena-600 dark:text-neon-200/55 font-normal">(cap {BULK_CAP})</span>
          </div>
          {bulkResult && (
            <span className="text-[11.5px] text-arena-700 dark:text-neon-200/75 ml-1 truncate">
              · {bulkResult}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} disabled={selected.size === 0}
              className="text-[12px] font-semibold text-arena-700 dark:text-neon-200/75 px-2 py-1 rounded-full hover:bg-arena-50 dark:hover:bg-arena-700/60 disabled:opacity-40">
              Clear
            </button>
            <button onClick={bulkSuspend} disabled={selected.size === 0 || bulkBusy}
              className="inline-flex items-center gap-1 text-[12px] font-bold text-white px-3 py-1.5 rounded-full
                         bg-orange-600 hover:bg-orange-500 disabled:opacity-40">
              {bulkBusy ? <Icon name="progress_activity" size={12} className="animate-spin" /> : <Icon name="block" size={12} />}
              Suspend {selected.size}
            </button>
          </div>
        </div>
      )}

      {creating && <PromoteModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load(); }} />}
      {reassigning && (
        <AgentReassignSheet
          subject={{ kind: 'agent', label: reassigning.username, currentParentId: reassigning.parent_agent_id }}
          excludeAgentIds={[reassigning.id]}
          detach={{ label: 'Detach to admin direct', description: 'Removes parent agent. Agent reports directly to admin.', icon: 'shield' }}
          preview={{ agentId: reassigning.id }}
          onClose={() => setReassigning(null)}
          onAssign={async (newParentId, reason) => {
            await adminApi.post(`/admin/agents/${reassigning.id}/reassign-parent`, {
              new_parent_agent_id: newParentId,
              reason: reason || undefined,
            });
            setReassigning(null);
            void load();
          }}
        />
      )}
      {actioning && (
        <AgentActionSheet
          a={actioning}
          isAdmin={isAdmin}
          navigate={navigate}
          onClose={() => setActioning(null)}
          onReassign={() => { const r = actioning; setActioning(null); setReassigning(r); }}
          onResetPassword={() => { const r = actioning; setActioning(null); setResetting(r); }}
          onAdjust={() => { const r = actioning; setActioning(null); setAdjusting(r); }}
          onMutated={async () => { setActioning(null); await load(); }}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          agent={resetting}
          onClose={() => setResetting(null)}
          onDone={() => { setResetting(null); void load(); }}
        />
      )}
      {adjusting && (
        <DirectWalletAdjustModal
          subject={{
            kind: 'agent',
            id: adjusting.id,
            username: adjusting.username,
            // Pass the rich identity fields so the modal can render
            // the AGENT identity card with avatar + level + UAID +
            // parent + pool (FLAW polish 2026-05-16).
            avatarUrl: adjusting.avatar_url,
            level: adjusting.level,
            uaid: adjusting.uaid,
            parentUsername: adjusting.parent_username,
            // parent_agent_id lets the modal fetch + show the parent's
            // level + pool in the upline-context chip. Null when this
            // agent is admin-direct.
            parentAgentId: adjusting.parent_agent_id,
            poolBalance: adjusting.wallet_balance,
          }}
          onClose={() => setAdjusting(null)}
          onDone={() => { setAdjusting(null); void load(); }}
        />
      )}
    </div>
  );
}

// ─── Agent card ────────────────────────────────────────────────────

function AgentCard({
  a, index, bulkMode, selected, onToggleSelect, onOpenActions,
}: {
  a: AgentRow;
  index: number;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenActions: () => void;
}) {
  const locked   = !!a.identity_locked;
  const lockedUntilTs = a.locked_until ? new Date(a.locked_until) : null;
  const isLockedOut = !!lockedUntilTs && lockedUntilTs > new Date();
  const isTest   = !!a.is_test;
  const acceptingPlayers = !!a.is_accepting_players;
  const acceptingAgents  = !!a.is_accepting_agents;
  const depTarget = Number(a.security_deposit_target_points || 0);
  const depShortfall = depTarget > 0 ? Math.max(0, depTarget - a.wallet_balance) : 0;

  function copy(text: string, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    if (!text) return;
    try { void navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
  }

  // Mobile-first redesign (2026-05-16):
  //   • Tighter padding on phone (p-3) vs desktop (sm:p-4)
  //   • Identity row compresses L-pill + UAID inline; long UAIDs
  //     truncate via `truncate` rather than overflow off-screen
  //   • Single money strip uses flex (not 2-col grid) — wallet on
  //     the left, deposit/last-login on the right, both compact
  //   • Single downline strip — "Agents 2+2 · Players 4+0"
  //     compressed format keeps the row on one line at 360px
  //   • Removed the inline REASSIGN PARENT button (already in kebab)
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.015, 0.18) }}
      className={`group relative rounded-2xl
                  bg-white dark:bg-arena-700/40
                  ring-1 ring-inset ${selected
                    ? 'ring-emerald-400/70 dark:ring-emerald-400/60'
                    : 'ring-arena-200/55 dark:ring-arena-600/40'}
                  transition`}
    >
      {bulkMode && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(); }}
          className={`absolute top-2.5 left-2.5 z-10 grid place-items-center w-6 h-6 rounded-md
                      ${selected
                        ? 'bg-emerald-600 text-white ring-1 ring-emerald-600'
                        : 'bg-white dark:bg-arena-800 text-arena-500 dark:text-neon-200/60 ring-1 ring-inset ring-arena-300/70 dark:ring-arena-600/40'}`}
          aria-label={selected ? 'Deselect agent' : 'Select agent'}
        >
          {selected && <Icon name="check" size={14} />}
        </button>
      )}

      <button
        onClick={onOpenActions}
        className="absolute top-2 right-2 z-10 grid place-items-center w-9 h-9 rounded-full
                   text-arena-500 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60
                   active:bg-arena-200/60 dark:active:bg-arena-700"
        aria-label="More actions"
      >
        <Icon name="more_horiz" size={18} />
      </button>

      <Link
        to={`/people/agents/${a.id}`}
        onClick={(e) => { if (bulkMode) { e.preventDefault(); onToggleSelect(); } }}
        className={`block p-3 sm:p-4 ${bulkMode ? 'pl-10' : ''} pr-11
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 rounded-2xl`}
      >
        {/* Row 1: avatar + identity column + status pill */}
        <div className="flex items-start gap-2.5 sm:gap-3">
          <AdminAvatar url={a.avatar_url} username={a.username} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-1.5">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] sm:text-[14.5px] font-semibold text-neutral-900 dark:text-white truncate leading-tight flex items-center gap-1.5">
                  <span className="truncate">@{a.username}</span>
                  <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">
                    {levelName(a.level)}
                  </span>
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/65 truncate leading-tight mt-0.5">
                  {a.parent_username ? `under @${a.parent_username}` : 'admin direct'}
                  <span className="text-arena-400 dark:text-neon-200/70"> · #{a.id}</span>
                </div>
                {a.uaid && (
                  <button
                    onClick={(e) => copy(a.uaid!, e)}
                    className="mt-0.5 inline-flex items-center gap-0.5 font-mono font-semibold text-[10px]
                               text-arena-700 dark:text-neon-200/80
                               hover:text-emerald-700 dark:hover:text-emerald-300"
                    aria-label="Copy UAID"
                  >
                    <Icon name="content_copy" size={9} /> {a.uaid}
                  </button>
                )}
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold capitalize ${STATUS_PILL[a.status]}`}>
                <span className="w-1 h-1 rounded-full bg-current" />
                {a.status}
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: chip row — flags only, no labels-as-chips. Skipped when nothing to show. */}
        {(a.suspended_at || locked || isLockedOut || isTest) && (
          <div className="mt-2 flex items-center gap-1 flex-wrap text-[10px]">
            {a.suspended_at && (
              <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                               bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60
                               dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/30">
                <Icon name="warning" size={9} filled /> auto-susp
              </span>
            )}
            {locked && (
              <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                               bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60
                               dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30">
                <Icon name="lock" size={9} filled /> id locked
              </span>
            )}
            {isLockedOut && (
              <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                               bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60
                               dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30">
                <Icon name="lock_clock" size={9} filled /> locked out
              </span>
            )}
            {isTest && (
              <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                               bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70
                               dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
                <Icon name="science" size={9} filled /> test
              </span>
            )}
          </div>
        )}

        {/* Row 3: money strip — pool on left, deposit OR last-login on right */}
        <div className="mt-2.5 pt-2.5 border-t border-arena-200/40 dark:border-arena-700/40
                        flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className={`text-[14px] sm:text-[15px] font-display font-extrabold tabular-nums leading-none
              ${a.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
              {inr(a.wallet_balance)}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">pool</div>
          </div>
          <div className="text-right min-w-0">
            {depTarget > 0 ? (
              <>
                <div className={`text-[12px] font-bold tabular-nums leading-tight
                  ${depShortfall > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  {depShortfall > 0 ? `−${inr(depShortfall)}` : 'met'}
                </div>
                <div className="text-[10px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">deposit</div>
              </>
            ) : (
              <>
                <div title={a.last_login_at || ''} className="text-[12px] font-bold tabular-nums leading-tight text-neutral-900 dark:text-white">
                  {relTime(a.last_login_at)}
                </div>
                <div className="text-[10px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">last login</div>
              </>
            )}
          </div>
        </div>

        {/* Row 4: downline strip — compact "Agents 2+2 · Players 4+0" */}
        <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
          <div className="inline-flex items-center gap-1 min-w-0 truncate">
            <Icon name="support_agent" size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span className="text-arena-600 dark:text-neon-200/70">Agents</span>
            <span className="font-bold tabular-nums text-neutral-900 dark:text-white">
              {a.direct_agents}<span className="text-arena-400 dark:text-neon-200/70">+</span>{a.indirect_agents}
            </span>
          </div>
          <div className="inline-flex items-center gap-1 min-w-0 truncate">
            <Icon name="person" size={12} className="text-cyan-600 dark:text-cyan-400 shrink-0" />
            <span className="text-arena-600 dark:text-neon-200/70">Players</span>
            <span className="font-bold tabular-nums text-neutral-900 dark:text-white">
              {a.direct_players}<span className="text-arena-400 dark:text-neon-200/70">+</span>{a.indirect_players}
            </span>
          </div>
        </div>

        {/* Row 5: state-toggle chips */}
        <div className="mt-2 flex items-center gap-1 text-[10px]">
          <StateChip ok={acceptingPlayers} label="players" icon="person_add" />
          <StateChip ok={acceptingAgents}  label="agents"  icon="group_add" />
        </div>

        {/* Row 6 (optional): contact — single line at the bottom */}
        {(a.phone || a.email) && (
          <div className="mt-2 flex items-center gap-2 text-[10.5px] text-arena-600 dark:text-neon-200/55 leading-tight">
            {a.phone && (
              <PhoneContact phone={a.phone} name={a.username} iconSize={9}
                className="font-mono shrink-0 max-w-[45%] hover:text-arena-800 dark:hover:text-neon-200/85" />
            )}
            {a.email && (
              <button onClick={(e) => copy(a.email, e)}
                className="inline-flex items-center gap-0.5 min-w-0 hover:text-arena-800 dark:hover:text-neon-200/85 flex-1 truncate justify-end">
                <Icon name="content_copy" size={9} className="shrink-0" />
                <span className="truncate">{a.email}</span>
              </button>
            )}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

/**
 * StateChip — compact "taking X" / "not taking X" indicator. The
 * shorter `label` strings ("players" / "agents") keep both chips on
 * one row at 360px wide; the leading verb and the `not` prefix are
 * rendered explicitly so the chip stays self-describing.
 */
function StateChip({ ok, label, icon }: { ok: boolean; label: string; icon: string }) {
  const cls = ok
    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30'
    : 'bg-arena-50 text-arena-500 ring-1 ring-inset ring-arena-200/50 dark:bg-arena-700/40 dark:text-neon-200/45 dark:ring-arena-600/40';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold ${cls}`}>
      <Icon name={icon} size={10} filled={ok} />
      {ok ? `taking ${label}` : `not taking ${label}`}
    </span>
  );
}

// ─── Action sheet wrapper ──────────────────────────────────────────

function AgentActionSheet({
  a, isAdmin, navigate, onClose, onReassign, onResetPassword, onAdjust, onMutated,
}: {
  a: AgentRow;
  isAdmin: boolean;
  navigate: NavigateFunction;
  onClose: () => void;
  onReassign: () => void;
  onResetPassword: () => void;
  onAdjust: () => void;
  onMutated: () => Promise<void> | void;
}) {
  const isActive = a.status === 'active';
  const locked   = !!a.identity_locked;
  const isLockedOut = !!a.locked_until && new Date(a.locked_until) > new Date();
  const accPlayers = !!a.is_accepting_players;
  const accAgents  = !!a.is_accepting_agents;

  async function post(path: string, body?: unknown) {
    await adminApi.post(path, body);
    await onMutated();
  }
  async function patch(path: string, body?: unknown) {
    await adminApi.patch(path, body);
    await onMutated();
  }

  const groups: ActionGroup[] = [
    {
      title: 'Open',
      items: [
        { key: 'profile', icon: 'open_in_new', label: 'Open profile',
          hint: `View full record for @${a.username}`,
          onSelect: () => { navigate(`/people/agents/${a.id}`); } },
        { key: 'reassign', icon: 'swap_horiz', label: 'Reassign parent',
          hint: a.parent_username ? `Currently under @${a.parent_username}` : 'Currently admin-direct',
          onSelect: () => onReassign() },
      ],
    },
    {
      // FLAW 2 fix (2026-05-16): direct top-up / withdraw on the agent's
      // own pool. Same DirectWalletAdjustModal, agent subject — no
      // source toggle (admin is implicit counterparty), only direction.
      title: 'Pool',
      items: [
        { key: 'direct_adjust', icon: 'account_balance_wallet',
          label: 'Direct top-up / withdraw',
          hint: 'Mint to / burn from agent pool · ledger + audit',
          onSelect: () => { onAdjust(); } },
      ],
    },
    {
      title: 'Status',
      items: [
        { key: 'activate', icon: 'play_arrow', label: 'Activate', hidden: isActive,
          onSelect: () => patch(`/admin/agents/${a.id}`, { status: 'active' }) },
        { key: 'suspend', icon: 'block', label: 'Suspend', tone: 'danger', hidden: !isActive,
          confirm: { title: `Suspend @${a.username}?`, body: 'Their downline will lose them as a routing target until reactivated.', cta: 'Suspend', ctaTone: 'danger' },
          onSelect: () => patch(`/admin/agents/${a.id}`, { status: 'suspended' }) },
        { key: 'toggle_players', icon: accPlayers ? 'do_not_disturb_on' : 'person_add',
          label: accPlayers ? 'Stop taking players' : 'Start taking players',
          hint: 'Affects locator-based player routing only',
          onSelect: () => {
            // No dedicated admin endpoint — agent-side endpoint accepts admin token too
            // via meAgentSubAgents.setChildAcceptingPlayers (parent acting on direct child),
            // but admin can also use the agent setStatus + identity_lock path.
            // Simplest: piggyback on meAgentRouter.opt-out (works for self) — for admin
            // we use the explicit hierarchy/agent_status flag via AgentModel.setAcceptingPlayers,
            // exposed through PATCH /admin/agents/:id (status not actually changing).
            return patch(`/admin/agents/${a.id}`, { is_accepting_players: !accPlayers });
          } },
        { key: 'toggle_agents', icon: accAgents ? 'do_not_disturb_on' : 'group_add',
          label: accAgents ? 'Stop taking agents' : 'Start taking agents',
          hint: 'Gates new sub-agent reassignments into this slot',
          onSelect: () => patch(`/admin/agents/${a.id}`, { is_accepting_agents: !accAgents }) },
      ],
    },
    {
      title: 'Identity & access',
      items: [
        { key: 'idlock', icon: locked ? 'lock_open' : 'lock', label: locked ? 'Unlock identity edits' : 'Lock identity edits',
          hint: locked ? 'Agent can edit username / email / avatar / password' : 'Agent cannot self-edit identity fields',
          onSelect: () => post(`/admin/agents/${a.id}/identity-lock`, { locked: !locked }) },
        { key: 'clear_lockout', icon: 'lock_open', label: 'Clear auth lockout',
          hint: isLockedOut ? `Locked until ${new Date(a.locked_until!).toLocaleTimeString('en-IN')}` : 'No active lockout',
          disabled: !isLockedOut && a.failed_login_attempts === 0,
          onSelect: () => post(`/admin/agents/${a.id}/clear-lockout`) },
        { key: 'force_logout', icon: 'logout', label: 'Force logout', tone: 'danger',
          hint: 'Revokes every agent refresh token',
          confirm: { title: `Force log @${a.username} out everywhere?`, body: 'They will be punted to login on next API call. Password unchanged.', cta: 'Force logout', ctaTone: 'danger' },
          onSelect: () => post(`/admin/agents/${a.id}/force-logout`) },
        { key: 'reset_password', icon: 'password', label: 'Reset password', tone: 'danger' as const,
          hint: 'Rotates password AND kills every session',
          // No `confirm` here: the ResetPasswordModal IS the confirm — it
          // captures the new credential AND requires an explicit
          // submit click. Using EntityActionSheet's confirm prompt on
          // top of that would be a double dialog.
          onSelect: () => { onResetPassword(); } },
      ],
    },
    ...(isAdmin ? [{
      title: 'Admin only',
      items: [
        { key: 'test', icon: a.is_test ? 'science_off' : 'science',
          label: a.is_test ? 'Unmark test account' : 'Mark as test account',
          hint: a.is_test ? 'Will be counted in analytics again' : 'Will be hidden from analytics',
          onSelect: () => post(`/admin/agents/${a.id}/test-flag`, { is_test: !a.is_test }) },
        { key: 'delete', icon: 'delete_forever', label: 'Delete agent', tone: 'danger' as const,
          hint: 'Cascades wallet + tokens. Owned players are detached.',
          confirm: { title: `Permanently delete @${a.username}?`, body: 'This deletes the row + cascades wallet / tokens. Owned players are detached back to platform-direct. Cannot be undone.', cta: 'Delete', ctaTone: 'danger' as const },
          onSelect: async () => {
            await adminApi.del(`/admin/agents/${a.id}`);
            await onMutated();
          } },
      ],
    } satisfies ActionGroup] : []),
  ];

  return (
    <EntityActionSheet
      open
      onClose={onClose}
      title={`@${a.username}`}
      subtitle={`Agent #${a.id} · ${levelName(a.level)}${a.uaid ? ` · ${a.uaid}` : ''}`}
      groups={groups}
    />
  );
}

// ─── Promote modal (unchanged from previous version) ──────────────

function PromoteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  // 2026-05-18 — Mandatory identity fields. Backend promoteSchema
  // rejects creates without all three (first_name, last_name min 2
  // chars; birthdate YYYY-MM-DD). DB CHECK constraints back it up.
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [geo, setGeo] = useState<GeoBag>(EMPTY_GEO_BAG);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const geoSet = isGeoBagComplete(geo);
  // 2026-07-06 — optional parent + partnership (P&L) rates set at creation.
  type ParentOpt = { id: number; username: string; level: number; status?: string; rates: CommissionRates };
  const [parents, setParents] = useState<ParentOpt[]>([]);
  const [parentId, setParentId] = useState<number | ''>('');
  const [rates, setRates] = useState<CommissionRates>({});
  useEffect(() => {
    adminApi.get<{ agents: ParentOpt[] }>('/admin/commission/agents')
      .then((r) => { setParents(r.agents.filter((a) => a.level < 3 && a.status !== 'suspended')); })
      .catch(() => {});
  }, []);
  const parent = parents.find((p) => p.id === parentId) || null;
  const newLevel = parent ? parent.level + 1 : 1;
  // Golden-limit ceilings for the PARTNERSHIP (P&L) rates — ALWAYS shown so the
  // operator sees the max they can grant. Under a parent → the parent's own rates
  // (win/loss STRICT = parent − 0.01, cricket P&L inclusive). Top-level → 100%.
  // Coerce a rate field (number | '' from the editable form model) to a numeric
  // ceiling or undefined — an unset ('') sport has no explicit cap (Partial key omitted).
  const ceilNum = (v: number | '' | null | undefined): number | undefined =>
    (v === '' || v == null ? undefined : Number(v));
  const ceilings: Partial<Record<keyof CommissionRates, number>> = parent ? {
    winLossPct: Math.max(0, Math.round(((parent.rates.winLossPct ?? 0) as number) * 100 - 1) / 100),
    profitSharePct: ceilNum(parent.rates.profitSharePct),
    // C3 (2026-08-06): per-sport ceilings so football/tennis show the parent's own cap,
    // not the cricket cap (backend falls these back to cricket when a sport is unset).
    cricketPct: ceilNum(parent.rates.cricketPct),
    footballPct: ceilNum(parent.rates.footballPct),
    tennisPct: ceilNum(parent.rates.tennisPct),
  } : {
    winLossPct: 100, profitSharePct: 100,
  };
  // 2026-05-25 — phone now mandatory + validated (unique on the server).
  const phoneValid = /^\+?\d{7,15}$/.test(phone.trim());
  const namesValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length  >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(birthdate);

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        username, email, password,
        phone: phone.trim(),
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        birthdate,
      };
      if (geo.latitude    != null) body.latitude    = geo.latitude;
      if (geo.longitude   != null) body.longitude   = geo.longitude;
      if (geo.state_id    != null) body.state_id    = geo.state_id;
      if (geo.district_id != null) body.district_id = geo.district_id;
      if (geo.area_id     != null) body.area_id     = geo.area_id;
      if (geo.nation)              body.nation      = geo.nation;
      if (parentId !== '')         body.parent_agent_id = parentId;
      const rp = ratesPayload(rates);
      if (rp)                      body.rates       = rp;
      await adminApi.post('/admin/agents', body);
      onCreated();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 360, damping: 28 }}
        className="w-full max-w-md rounded-2xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   p-5 space-y-3 shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-lg text-neutral-900 dark:text-white inline-flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-xl
                           bg-emerald-50 text-emerald-700
                           dark:bg-emerald-400/15 dark:text-emerald-300">
            <Icon name="support_agent" size={18} filled />
          </span>
          Promote a new agent
        </h3>
        <p className="text-[12px] text-arena-700 dark:text-neon-200/75">
          Creates an agent with default permissions. Pick a parent to place them deeper in the hierarchy, and set
          their partnership (P&amp;L) % right here so nothing is missed.
        </p>

        {/* Parent + derived level. No parent → a top-level Super Master. */}
        <Field label="Create under (parent)">
          <select className={inputCls} value={parentId}
            onChange={(e) => { setParentId(e.target.value === '' ? '' : Number(e.target.value)); setRates({}); }}>
            <option value="">— Top-level (Super Master) —</option>
            {parents.map((p) => (
              <option key={p.id} value={p.id}>{p.username} · {levelName(p.level)} → new {levelName(p.level + 1)}</option>
            ))}
          </select>
        </Field>
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-arena-500 dark:text-neon-200/60">This agent will be a</span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">
            {levelName(newLevel)}
          </span>
          {parent && <span className="text-arena-500 dark:text-neon-200/60">under @{parent.username}</span>}
        </div>
        {/* 2026-05-18 — Mandatory display-name fields. Backend rejects
            creates missing any of them; the DB CHECK constraints
            reject empty / whitespace / <2-char values. */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="First name *">
            <input className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Rajesh" maxLength={80} />
          </Field>
          <Field label="Last name *">
            <input className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Sharma" maxLength={80} />
          </Field>
        </div>
        <Field label="Date of birth *">
          {/* 2026-05-18 — Tighter max-width so the fixed-length date
              input doesn't dominate the modal on mobile. */}
          <input type="date" className={inputCls + ' max-w-[10rem] sm:max-w-[12rem]'} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
        </Field>
        <Field label="Username">
          <input className={inputCls} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="agent_kumar" />
        </Field>
        <Field label="Email">
          <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kumar@example.com" />
        </Field>
        <Field label="Phone *">
          <input type="tel" className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" maxLength={16} />
          {phone.trim() && !phoneValid && (
            <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1">Enter a valid phone (7–15 digits, optional +).</p>
          )}
        </Field>
        <Field label="Initial password (8+ chars, letter + digit)">
          <input type="text" className={inputCls + ' font-mono'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="StrongP4ss" />
        </Field>

        <div className="pt-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1.5">
            New agent's location <span className="font-normal lowercase opacity-70">(strongly recommended)</span>
          </div>
          <LocationCapture
            value={geo}
            onChange={setGeo}
            mode="child"
            showSavedSummary={false}
            disabled={busy}
          />
          {!geoSet && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1.5">
              Without a location, the geographic locator can't route new players to this agent. They'll only receive players via direct referral until they sign in and complete their own GPS.
            </p>
          )}
        </div>

        <CommissionRatesFields value={rates} onChange={(patch) => setRates((r) => ({ ...r, ...patch }))} ceilings={ceilings} disabled={busy} />

        {err && <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/75
                       hover:bg-arena-50 dark:hover:bg-arena-700/60">Cancel</button>
          <button onClick={submit}
            disabled={busy || !username || !email || !phoneValid || password.length < 8 || !namesValid}
            title={!phoneValid ? 'A valid phone number is required' : (!namesValid ? 'First name, last name (≥2 chars each), and date of birth are required' : undefined)}
            className="px-4 py-2 rounded-full text-[13px] font-bold
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]
                       inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
            Promote
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// 2026-05-20 — aliased to canonical INPUT_CLS for dark-mode legibility.
const inputCls = INPUT_CLS;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}

// ─── Reset-password modal ──────────────────────────────────────────
//
// Replaces the previous prompt()-based capture. The modal:
//   • Locks focus and Esc-closes (delegated to the backdrop)
//   • Validates 8+ chars + at least one letter + at least one digit
//     CLIENT-SIDE so the user sees the gate before submit. The server
//     enforces the same rule via the resetPassword controller's Zod
//     schema as the source of truth.
//   • Offers a show/hide toggle so admin can verify what they typed
//     before clicking — important when sharing the credential out-of-
//     band via WhatsApp/call.
//   • Provides a "Suggest" button that drops a random 12-char
//     password into the field (alphabet excludes 0/O/1/I/l for
//     dictability over voice channels).
//   • Surfaces server errors verbatim (e.g. "Validation error") so a
//     mismatch with the server schema is visible.
//
// On success we call onDone which closes the modal AND reloads the
// agents list so the post-reset row reflects (in future revisions)
// last_password_reset_at if we ever surface it.
function ResetPasswordModal({
  agent, onClose, onDone,
}: {
  agent: AgentRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const tooShort   = pw.length > 0 && pw.length < 8;
  const noLetter   = pw.length > 0 && !/[A-Za-z]/.test(pw);
  const noDigit    = pw.length > 0 && !/\d/.test(pw);
  const valid      = pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  function suggest() {
    // 12-char, voice-dictable alphabet (excludes 0/O/1/I/l, plus
    // visually-confusable B/8 and 5/S since admins read these aloud
    // when handing off the credential). Resulting space is still
    // 24^12 ≈ 7.9 × 10^16 — orders of magnitude beyond brute-force.
    const alpha = 'ABCDEFGHJKLMNPQRTUVWXYZabcdefghjkmnpqrtuvwxyz';
    const digs  = '23456789';
    const chars: string[] = [];
    // Guarantee at least one letter and one digit by seeding both
    // before filling out to length 12 with mixed pool.
    chars.push(alpha[Math.floor(Math.random() * alpha.length)]);
    chars.push(digs[Math.floor(Math.random() * digs.length)]);
    const pool = alpha + digs;
    while (chars.length < 12) chars.push(pool[Math.floor(Math.random() * pool.length)]);
    // Fisher-Yates so the seeded letter/digit aren't always in the
    // first two positions.
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    setPw(chars.join(''));
    setShow(true);
  }

  async function submit() {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${agent.id}/reset-password`, { new_password: pw });
      onDone();
    } catch (e) {
      setErr((e as ApiError).message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    try { void navigator.clipboard.writeText(pw); } catch { /* best-effort */ }
  }

  return (
    <div className="fixed inset-0 z-[85] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 360, damping: 28 }}
        className="w-full max-w-md rounded-2xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   p-5 space-y-3 shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-lg text-neutral-900 dark:text-white inline-flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-xl
                           bg-rose-50 text-rose-700
                           dark:bg-rose-500/15 dark:text-rose-300">
            <Icon name="password" size={18} filled />
          </span>
          Reset password — @{agent.username}
        </h3>
        <p className="text-[12.5px] text-arena-700 dark:text-neon-200/75 leading-snug">
          This rotates the agent's password AND signs them out of every device. Share the new credential
          out-of-band (WhatsApp / call). The agent must use this exact string to sign in again.
        </p>

        <div>
          <div className="flex items-center justify-between">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 font-semibold">
              New password
            </span>
            <button onClick={suggest} disabled={busy} type="button"
              className="text-[11px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 hover:underline disabled:opacity-40">
              <Icon name="autorenew" size={11} className="inline align-text-bottom mr-0.5" />
              Suggest
            </button>
          </div>
          <div className="relative mt-1">
            <input
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="8+ chars · letter + digit"
              disabled={busy}
              className={inputCls + ' font-mono pr-20'}
            />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <button onClick={() => setShow((v) => !v)} type="button" disabled={busy}
                className="p-1.5 rounded-full text-arena-600 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
                aria-label={show ? 'Hide password' : 'Show password'}>
                <Icon name={show ? 'visibility_off' : 'visibility'} size={16} />
              </button>
              <button onClick={copy} type="button" disabled={busy || !pw}
                className="p-1.5 rounded-full text-arena-600 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
                aria-label="Copy password">
                <Icon name="content_copy" size={15} />
              </button>
            </div>
          </div>
          <ul className="text-[11px] mt-1.5 space-y-0.5">
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : tooShort ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : tooShort ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              At least 8 characters
            </li>
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : noLetter ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : noLetter ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              Contains a letter
            </li>
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : noDigit ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : noDigit ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              Contains a digit
            </li>
          </ul>
        </div>

        {err && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/75
                       hover:bg-arena-50 dark:hover:bg-arena-700/60">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !valid}
            className="px-4 py-2 rounded-full text-[13px] font-bold
                       bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                       text-white shadow-[0_2px_10px_rgba(244,63,94,0.35)]
                       inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="password" size={14} />}
            Reset & sign-out everywhere
          </button>
        </div>
      </motion.div>
    </div>
  );
}
